import React from 'react';
import { unpackToTime } from '@campfire/hot-date';
import { TitularTooltip } from '@campfire/titular-tooltip';
import { Box, Typography } from '@material-ui/core';
import { LocationOnRounded, Explore } from '@material-ui/icons';
import { DateInfoCard } from '../../../../common/cards/date-info-card/DateInfoCard';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { VolunteerRosters } from '../ActivityQueries/__generated__/VolunteerRosters';
import { RosteredStatus } from '../ActivityTypes';
import { getDisplayTimeSchedule } from '../../../../common/functions/activity-display-helpers';

import { ActivityTimelineItem as ActivityTimelineItemFragments } from '../ActivityTimeline/__generated__/ActivityTimelineItem';

export type MyShiftItemProps = ActivityTimelineItemFragments & {
  activityDate: string;
  rosterTemplateAddress?: string;
};

export interface MyShiftItemMenuProps {
  rosteredStatus: RosteredStatus;
  onUpdateAvailability: () => void;
  onUnavailable: () => void;
  onWithdraw: () => void;
}

export function MyShiftItem(props: {
  activity: MyShiftItemProps;
  ActivityMenu: any;
  onSelect: () => void;
  isSelected: boolean;
  color: any;
  rostering?: VolunteerRosters;
  conflict?: boolean;
}) {
  const { activity, onSelect, isSelected, ActivityMenu, color, rostering, conflict } = props;
  const { theme, isMobile, isMd } = useCampfireTheme();
  const formatTime = (time: string) => unpackToTime(time).toFormat('h:mm a');

  const renderActivityTime = (
    activityStartTime: string,
    activityEndTime: string,
    activitySessions?: any[],
    dateForActivity?: string
  ) => {
    // Check if user is rostered for any sessions
    const rosteredSessionIds = rostering?.sessionRosterings.map((sr) => sr.session.sessionId) || [];

    // Find all rostered sessions
    const rosteredSessions =
      activitySessions?.filter((session) => rosteredSessionIds.includes(session.sessionId)) || [];

    // If we have rostered sessions, calculate the latest end time
    let latestEndTime = '';
    let earliestStartTime = '';

    // First check if we have sessions with flexible instances for the current date
    if (activitySessions && dateForActivity) {
      // Look for flexible sessions with instances for this date
      const flexibleSessionsWithInstances = activitySessions.filter(
        (session) =>
          session.isFlexible &&
          session.currentFlexibleInstances &&
          session.currentFlexibleInstances.length > 0 &&
          session.currentFlexibleInstances[0] &&
          (!session.currentFlexibleInstances[0].sessionDate ||
            session.currentFlexibleInstances[0].sessionDate === dateForActivity)
      );

      if (flexibleSessionsWithInstances.length > 0) {
        // For rostered sessions, track the earliest start and latest end time
        if (rosteredSessions.length > 0) {
          // Get the earliest start time and latest end time from all rostered sessions
          rosteredSessions.forEach((session) => {
            // For flexible sessions, use the instance time
            if (session.isFlexible && session.currentFlexibleInstances && session.currentFlexibleInstances.length > 0) {
              const instance = session.currentFlexibleInstances[0];
              if (instance.startTime && (!earliestStartTime || instance.startTime < earliestStartTime)) {
                earliestStartTime = instance.startTime;
              }
              if (instance.endTime && (!latestEndTime || instance.endTime > latestEndTime)) {
                latestEndTime = instance.endTime;
              }
            }
            // For regular sessions, use the session time
            else {
              if (session.startTime && (!earliestStartTime || session.startTime < earliestStartTime)) {
                earliestStartTime = session.startTime;
              }
              if (session.endTime && (!latestEndTime || session.endTime > latestEndTime)) {
                latestEndTime = session.endTime;
              }
            }
          });

          // If we have calculated valid times, return the time range
          if (earliestStartTime && latestEndTime) {
            return `${formatTime(earliestStartTime)} - ${formatTime(latestEndTime)}`;
          }
        }

        // If no rostered sessions or couldn't calculate times, show flexible session times
        const flexibleTimes = flexibleSessionsWithInstances
          .map((session) => {
            if (!session.currentFlexibleInstances || !session.currentFlexibleInstances.length) {
              return null;
            }
            const flexibleInstance = session.currentFlexibleInstances[0];
            if (flexibleInstance && flexibleInstance.startTime && flexibleInstance.endTime) {
              const timeStr = getDisplayTimeSchedule(
                flexibleInstance.startTime,
                flexibleInstance.endTime,
                true,
                flexibleInstance
              );

              // If we have multiple sessions, prefix with session name
              const displayName = flexibleInstance.name || session.name;
              return flexibleSessionsWithInstances.length > 1 ? `${displayName}: ${timeStr}` : timeStr;
            }
            return null;
          })
          .filter(Boolean);

        if (flexibleTimes.length > 0) {
          return flexibleTimes.join(', ');
        }
      }
    }

    // Default to regular activity time
    return activityStartTime && activityEndTime
      ? `${formatTime(activityStartTime)} - ${formatTime(activityEndTime)}`
      : '';
  };

  return (
    <Box marginTop='0.9375rem' onClick={onSelect}>
      <DateInfoCard date={activity.activityDate} size='l' color={color} selected={isSelected} conflict={conflict}>
        <Box display='flex' justifyContent='space-between' alignSelf='stretch'>
          <TitularTooltip title={activity.name}>
            <Typography
              variant={'h6'}
              noWrap
              style={{ color: theme.color.grey.neutral400, fontWeight: 800, width: '100%', letterSpacing: '-0.02em' }}
            >
              {activity.name}
            </Typography>
          </TitularTooltip>
          {ActivityMenu}
        </Box>
        {rostering ? (
          <Typography variant={'caption'} style={{ color: theme.color.grey.neutral200 }}>
            Attending:{' '}
            {rostering.sessionRosterings
              .map((sessionRostering) => {
                // Check if there's a matching session with flexible instances
                if (activity.sessions && activity.sessions.length > 0) {
                  const { sessionId } = sessionRostering.session;
                  const matchingSession = activity.sessions.find((session) => session.sessionId === sessionId);

                  // If we have a matching session with flexible instances
                  if (
                    matchingSession &&
                    matchingSession.isFlexible &&
                    matchingSession.currentFlexibleInstances &&
                    matchingSession.currentFlexibleInstances.length > 0
                  ) {
                    // Get the first flexible instance (which should be for the current date)
                    const flexibleInstance = matchingSession.currentFlexibleInstances[0];

                    // Use the instance name if available, or fall back to the session name
                    if (flexibleInstance.name) {
                      return flexibleInstance.name;
                    }
                  }
                }
                // Default to the original session name
                return sessionRostering.session.name;
              })
              .join(', ')}
          </Typography>
        ) : null}
        <Typography variant={'caption'} style={{ color: theme.color.grey.neutral200 }}>
          {renderActivityTime(activity.startTime, activity.endTime, activity.sessions, activity.activityDate)}
        </Typography>
        <Box display={isMobile || isMd ? 'auto' : 'flex'} alignItems='center' mt='40px'>
          <Box display='flex' alignItems='center' pr={2} pt={isMobile || isMd ? 1 : 0}>
            <LocationOnRounded style={{ color: theme.color.grey.neutral200, fontSize: 14, marginRight: '6px' }} />
            <Typography variant={'caption'} style={{ color: theme.color.grey.neutral200, marginTop: '1px' }}>
              {activity?.rosterTemplateAddress
                ? activity?.rosterTemplateAddress
                : activity.activityLocation
                ? activity.activityLocation.placesAddress.description
                : activity.activityRemoteLocation?.details}
            </Typography>
          </Box>
          {/* <Box display='flex' alignItems='center' pr={3} pt={isMobile || isMd ? 1 : 0}>
            <AccessTimeIcon style={{ color: theme.color.grey.neutral200, fontSize: 14, marginRight: '6px' }} />
            <Typography variant={'caption'} style={{ color: theme.color.grey.neutral200, marginTop: '1px' }}>
              {activity.hasOpenRoster ? 'Open Roster' : 'Managed Roster'}
            </Typography>
          </Box> */}
          <Box display='flex' alignItems='center' pr={2} pt={isMobile || isMd ? 1 : 0}>
            <Typography
              variant={'h2'}
              style={{ fontWeight: 600, color: theme.color.grey.neutral200, fontSize: 14, marginRight: '6px' }}
            >
              {activity.sessions.length}
            </Typography>
            <Typography variant={'caption'} style={{ color: theme.color.grey.neutral200, marginTop: '1px' }}>
              {activity.sessions.length > 1 ? 'sessions available' : 'session available'}
            </Typography>
          </Box>
          <Box display='flex' alignItems='center' pr={2} pt={isMobile || isMd ? 1 : 0}>
            <Explore style={{ color: theme.color.grey.neutral200, fontSize: 14, marginRight: '6px' }} />
            <Typography variant={'caption'} style={{ color: theme.color.grey.neutral200, marginTop: '1px' }}>
              {activity?.program.name}
            </Typography>
          </Box>
        </Box>
      </DateInfoCard>
    </Box>
  );
}
